import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { SocketProvider } from './contexts/SocketContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SongCataloguePage from './pages/SongCataloguePage';
import QueueAdminPage from './pages/QueueAdminPage';
import CurrentSongLyricsPage from './pages/CurrentSongLyricsPage';
import BandChartPage from './pages/BandChartPage';
import SongAdminPage from './pages/SongAdminPage';
import HomePage from './pages/HomePage';
import GigHomePage from './pages/GigHomePage';
import LoginPage from './pages/LoginPage';
import NowNextPage from './pages/NowNextPage';
import QueueAndVotingPage from './pages/QueueAndVotingPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import BandDashboardPage from './pages/BandDashboardPage';
import GigHistoryPage from './pages/GigHistoryPage';
import BandMemberAdminPage from './pages/BandMemberAdminPage';
import QRFlyerPage from './pages/QRFlyerPage';
import NewsletterAdminPage from './pages/NewsletterAdminPage';

// Navigation component that conditionally renders based on route and auth
const Navigation: React.FC = () => {
  const location = useLocation();
  const { member, isLoggedIn, isAdmin, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Don't show gig navigation on the marketing homepage
  if (location.pathname === '/') {
    return null;
  }

  const handleLogout = () => {
    logout();
    window.location.href = '/gig';
  };

  const dropdownLinkStyle: React.CSSProperties = {
    display: 'block',
    padding: '10px 20px',
    color: '#c8a060',
    textDecoration: 'none',
    fontWeight: 'bold',
    letterSpacing: '1px',
    borderBottom: '1px solid #3a2510',
  };

  const dividerStyle: React.CSSProperties = {
    padding: '8px 20px',
    color: '#ffd700',
    fontSize: '11px',
    fontWeight: 'bold',
    letterSpacing: '2px',
    textTransform: 'uppercase' as const,
    borderBottom: '1px solid #3a2510',
    backgroundColor: 'rgba(90,58,26,0.4)',
  };

  return (
    <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
      {/* Primary links */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Link to="/gig" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/images/rodeoke-logo.png" alt="Rodeoke" style={{ height: '32px', width: 'auto' }} />
        </Link>
        <Link to="/gig/catalogue">Songs</Link>
        <Link to="/gig/queue-voting">Queue</Link>
      </div>

      {/* Right side: login/user + hamburger */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }} ref={menuRef}>
        {isLoggedIn ? (
          <span style={{ color: '#c8a060', fontWeight: 500, fontSize: '14px' }}>
            {member?.displayName}
          </span>
        ) : (
          <Link to="/gig/login">Login</Link>
        )}

        {/* Hamburger button */}
        <button
          onClick={() => setMenuOpen(prev => !prev)}
          aria-label="Menu"
          style={{
            background: 'none',
            border: 'none',
            color: '#fffbe6',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px 8px',
            lineHeight: 1,
          }}
        >
          ☰
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            backgroundColor: '#28190f',
            border: '1px solid #5a3a1a',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            minWidth: '220px',
            zIndex: 1000,
            overflow: 'hidden',
          }}>
            <Link to="/gig" style={dropdownLinkStyle}>Gig Home</Link>
            <Link to="/gig/catalogue" style={dropdownLinkStyle}>Song Catalogue</Link>
            <Link to="/gig/nownext" style={dropdownLinkStyle}>Now & Next</Link>
            <Link to="/gig/queue-voting" style={dropdownLinkStyle}>Queue & Voting</Link>
            <Link to="/gig/gig-history" style={dropdownLinkStyle}>Gig History</Link>

            {/* Band member links */}
            {isLoggedIn && !isAdmin && (
              <Link to="/gig/band/dashboard" style={dropdownLinkStyle}>My Dashboard</Link>
            )}

            {/* Logged-in user links */}
            {isLoggedIn && (
              <Link to="/gig/admin/change-password" style={dropdownLinkStyle}>Change Password</Link>
            )}

            {/* Admin links */}
            {isAdmin && (
              <>
                <div style={dividerStyle}>Admin</div>
                <Link to="/gig/queue" style={dropdownLinkStyle}>Queue / Admin</Link>
                <Link to="/gig/lyrics" style={dropdownLinkStyle}>Current Song Lyrics</Link>
                <Link to="/gig/band" style={dropdownLinkStyle}>Band Chart View</Link>
                <Link to="/gig/admin/songs" style={dropdownLinkStyle}>Song Admin</Link>
                <Link to="/gig/admin/band-members" style={dropdownLinkStyle}>Band Members</Link>
                <Link to="/gig/admin/qr-flyer" style={dropdownLinkStyle}>QR Flyer</Link>
                <Link to="/gig/admin/newsletter" style={dropdownLinkStyle}>Newsletter</Link>
              </>
            )}

            {/* Login / Logout */}
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                style={{
                  ...dropdownLinkStyle,
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  borderBottom: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  color: '#e74c3c',
                }}
              >
                Logout
              </button>
            ) : (
              <Link to="/gig/login" style={{ ...dropdownLinkStyle, borderBottom: 'none' }}>Login</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// Protected route wrapper for admin-only pages
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/gig/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/gig" replace />;
  }

  return <>{children}</>;
};

// Protected route wrapper for logged-in users (band members or admin)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/gig/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <>
      <Navigation />
      <Routes>
        {/* Marketing homepage */}
        <Route path="/" element={<HomePage />} />

        {/* Public gig routes */}
        <Route path="/gig" element={<GigHomePage />} />
        <Route path="/gig/catalogue" element={<SongCataloguePage />} />
        <Route path="/gig/nownext" element={<NowNextPage />} />
        <Route path="/gig/queue-voting" element={<QueueAndVotingPage />} />
        <Route path="/gig/gig-history" element={<GigHistoryPage />} />
        <Route path="/gig/login" element={<LoginPage />} />

        {/* Band member routes (logged in) */}
        <Route path="/gig/band/dashboard" element={
          <ProtectedRoute><BandDashboardPage /></ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/gig/queue" element={
          <AdminRoute><QueueAdminPage /></AdminRoute>
        } />
        <Route path="/gig/lyrics" element={
          <AdminRoute><CurrentSongLyricsPage /></AdminRoute>
        } />
        <Route path="/gig/band" element={
          <AdminRoute><BandChartPage /></AdminRoute>
        } />
        <Route path="/gig/admin/songs" element={
          <AdminRoute><SongAdminPage /></AdminRoute>
        } />
        <Route path="/gig/admin/band-members" element={
          <AdminRoute><BandMemberAdminPage /></AdminRoute>
        } />
        <Route path="/gig/admin/qr-flyer" element={
          <AdminRoute><QRFlyerPage /></AdminRoute>
        } />
        <Route path="/gig/admin/newsletter" element={
          <AdminRoute><NewsletterAdminPage /></AdminRoute>
        } />
        <Route path="/gig/admin/change-password" element={
          <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
        } />

        {/* Legacy redirect for old band login URL */}
        <Route path="/gig/band/login" element={<Navigate to="/gig/login" replace />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SocketProvider>
        <Router>
          <AppRoutes />
        </Router>
      </SocketProvider>
    </AuthProvider>
  );
};

export default App;
